import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#EEF2F6]" data-testid="login-page">
      {/* LEFT: brand + workspace image */}
      <div className="hidden md:flex flex-col relative overflow-hidden bg-[#0E1620]">
        <div className="h-2 brand-gradient relative z-20" />
        <img
          src="/login-bg.jpg"
          alt="Estudio de diseño TAS Publicidad 360"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1017] via-[#0B1017]/55 to-[#0B1017]/20" />

        <div className="relative z-10 flex-1 flex flex-col justify-between px-12 py-12">
          <img
            src="/tas-logo-dark.webp"
            alt="TAS Publicidad 360"
            className="w-52 drop-shadow-2xl"
            data-testid="login-brand-logo"
          />
          <div className="max-w-md">
            <h2 className="font-display font-bold text-white text-xl md:text-2xl uppercase tracking-[0.35em] leading-relaxed">
              Technology &amp; Advertising Solutions
            </h2>
            <p className="font-mono-data text-sm text-white/80 mt-5">
              Cotización → Remisión → Orden de trabajo → Facturación → Cobranza.
              Todo tu ciclo comercial en un solo lugar.
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT: form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <img src="/tas-logo.webp" alt="TAS Publicidad 360" className="w-40 mb-8 md:hidden" />
          <div className="overline">Bienvenido</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#16202B] mt-1 mb-2">
            Iniciar sesión
          </h1>
          <p className="text-sm text-[#5B6672] mb-8">
            Ingresa tus credenciales para acceder al panel.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <Field
              label="Correo"
              type="email"
              value={email}
              onChange={setEmail}
              testid="email-input"
              icon={Mail}
              placeholder="tucorreo@taspublicidad.mx"
            />

            <div className="block">
              <span className="overline block mb-2">Contraseña</span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A94A0]" />
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  required
                  data-testid="password-input"
                  placeholder="••••••••"
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#E3E8EE] pl-9 pr-10 py-2.5 font-mono-data text-sm text-[#1C1A17] focus:outline-none focus:ring-1 focus:ring-[#465B7F] focus:border-[#465B7F]"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  data-testid="toggle-password"
                  aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A94A0] hover:text-[#465B7F] transition-colors"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-button"
              className="w-full bg-[#465B7F] hover:bg-[#374A66] text-white uppercase tracking-widest text-sm py-3 flex items-center justify-center gap-2 transition-colors duration-150 disabled:opacity-60"
            >
              {loading ? "…" : (<>Entrar <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-8 border border-[#E3E8EE] bg-white p-4 text-xs text-[#5B6672] font-mono-data">
            El acceso es gestionado por el administrador.<br />
            Demo: admin@servicios.mx / admin123
          </div>

          <div className="mt-6 flex items-center justify-between text-xs text-[#8A94A0]">
            <span>© {new Date().getFullYear()} TAS Publicidad 360</span>
            <span className="font-mono-data">Impresión · Offset · Digital · Gran formato</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, icon: Icon, placeholder }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A94A0]" />}
        <input
          type={type}
          value={value}
          required
          data-testid={testid}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full bg-white border border-[#E3E8EE] ${Icon ? "pl-9" : "pl-3"} pr-3 py-2.5 font-mono-data text-sm text-[#1C1A17] focus:outline-none focus:ring-1 focus:ring-[#465B7F] focus:border-[#465B7F]`}
        />
      </div>
    </label>
  );
}
